// © Microsoft Corporation. All rights reserved.

/** Type info of a single page measure in the window._pageMeasures object */
export type PageMeasure = {

    /** Name of the timeline */
    timelineName: string;

    /** Name of the metric */
    measureName: string;

    /** Flag that indicates whether this measure is an async measure */
    async?: boolean;

    /** The start time of the measure */
    from: number;

    /** The end time of the measure */
    to: number;
};

/**
 * Interface for PerfTimeline constructor
 *
 * Reason for constructor interface is to
 * prevent PerfTimeline implementation and dependencies
 * from added to inline script bundle (eg. Edge Legacy service-ui)
 * when perftimeline is disabled.
*/
interface PerfTimelineConstructor {
    /**
     * New method to construct a {PerfTimeline} instance
     * @param timelineName - the name of the perf timeline. Note that if you don't know the name
     *  of a perf timeline at construction time, it can later be renamed to a known name later via
     *  the 'rename' method.
     * @param instanceId - this specific instance's ID. Used during a renaming operation to idenfity any previous isntance.
     * @param addPerfMarks - Flag to indicate whether to log perf marks with the native performance API.
     * @param addPageTimings - Flag to indicate whether to log the perf marks into the _pageTimings object.
     * @param async - Flag to mark this timeline as an async timeline, so by default all measures under
     *  this timeline will show under a new process line.
     */
    new (timelineName: string,
        instanceId: string,
        addPerfMarks: boolean,
        addPageTimings: boolean,
        async?: boolean): PerfTimeline;
}

/**
 * Factory method to construct a {PerfTimeline} instance
 * @param timelineName - the name of the perf timeline. Note that if you don't know the name
 *  of a perf timeline at construction time, it can later be renamed to a known name later via
 *  the 'rename' method.
 * @param instanceId - this specific instance's ID. Used during a renaming operation to idenfity any previous isntance.
 * @param addPerfMarks - Flag to indicate whether to log perf marks with the native performance API.
 * @param addPageTimings - Flag to indicate whether to log the perf marks into the _pageTimings object.
 * @param async - Flag to mark this timeline as an async timeline, so by default all measures under
 *  this timeline will show under a new process line.
 */
export function createPerfTimeline(
    ctor: PerfTimelineConstructor,
    timelineName: string,
    instanceId: string,
    addPerfMarks = false,
    addPageTimings = false,
    async?: boolean
): PerfTimeline {
    return new ctor(timelineName, instanceId, addPerfMarks, addPageTimings, async);
}

/** PerfTimeline interface for diagnostic utilities and its clients. */
export interface PerfTimeline {

    /**
     * the name of the perf timeline. Note that if you don't know the name
     * of a perf timeline at construction time, it can later be renamed to a known name later via
     * the 'rename' method.
     */
    readonly timelineName: string;

    /** this specific instance's ID. Used during a renaming operation to idenfity any previous isntance. */
    readonly instanceId: string;

    /** Renames this instances of the timeline to the new given name. */
    rename(timelineName: string): void;

    /** Marks the start of a timeline measurement. */
    startMeasure(coreMarkerName: string, dynamicMeasureName?: string, async?: boolean): {
        /** Method that should be used to mark the end the measure */
        endMeasure: (updatedDynamicMeasureName?: string) => void
    };

    /** Returns the list of page measures for this timeline object */
    getPageMeasures(): Array<PageMeasure>;

    /** Instruments the start/begin for the given function with a given binding context */
    instrumentFunc<TFunc extends Function>(funcName: string, func: TFunc, bindingContext?: Object): TFunc;

    /** Instruments the start/begin for the given async-style function */
    instrumentPromise<TResult>(promiseName: string, getPromise: () => Promise <TResult>, async?: boolean): Promise <TResult>;
}

/** Main factory function interface for perf timeline. */
export interface TryGetPerfTimelineFunc {
    /**
     * Gets or creates a perf timeline object for the given experience instance
     * @param experienceName - the name of the experience for which to create the timeline
     * @param instanceId - the ID that will uniquely identify this timeline across multiple instances.
     * @param async - Optional flag to indicate the default tracing mode for this timeline.
     *  Specifying true will render the measures under its own process id in the app trace.
     *  Useful if the timeline will contain many async operations that frequently overlap with each other.
     *  Note that this value can be overriden when taking an individual measure as well.
     */
    (experienceName: string, instanceId: string, async?: boolean): PerfTimeline;
}

/** Internal function reference for the perf timeline factory method */
let tryGetPerfTimelineFuncRef: TryGetPerfTimelineFunc = null;

/** Set the function to use to get PerfTimeline. This is used by initializeExperienceDiagnotics. */
export function setTryGetPerfTimelineFunc(func: TryGetPerfTimelineFunc): void {
    tryGetPerfTimelineFuncRef = func;
}

/**
 * Gets or creates a perf timeline object for the given experience instance
 * @param experienceName - the name of the experience for which to create the timeline
 * @param instanceId - the ID that will uniquely identify this timeline across multiple instances.
 * @param async - Optional flag to indicate the default tracing mode for this timeline.
 *  Specifying true will render the measures under its own process id in the app trace.
 *  Useful if the timeline will contain many async operations that frequently overlap with each other.
 *  Note that this value can be overriden when taking an individual measure as well.
 */
export const tryGetPerfTimeline: TryGetPerfTimelineFunc = (experienceName: string, instanceId: string, async?: boolean): PerfTimeline => {
    if (tryGetPerfTimelineFuncRef) {
        return tryGetPerfTimelineFuncRef(experienceName, instanceId, async);
    } else {
        return null;
    }
};
